import {
    Button, Col, Form, Input, message, Row, Spin, Typography,
} from 'antd';
import { useState, useEffect, useMemo } from 'react';
import CardDataBase from '../../../CardDataBase';
import { TUpdateWorkspaceBillingInfo, TWorkspaceBillingInfo } from '../../../../types/workspace-billing-info';
import { getWorkspaceBillingInfo, updateWorkspaceBillingInfo } from '../../../../api/workspace-billing-info';
import { EWorkspaceCountry } from '../../../../types/workspaces';
import { getBillingInfoFromCountry } from '../utils/workspace';
import InputEditable from '../../../InputEditable';

interface IBillingDetail {
    workspaceID: string;
    country: EWorkspaceCountry;
    ownerID: string;
}

const BillingDetail = ({
    workspaceID,
    country,
    ownerID,
} : IBillingDetail) => {
    const [form] = Form.useForm();

    const [isLoading, setIsLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const billingInfo = form.getFieldsValue();

    const CARD_CONTENT = useMemo(() => {
        const content = [
            {
                key: 'taxId',
                title: 'Tax number',
                content: billingInfo?.taxId,
            },
            {
                key: 'businessName',
                title: 'Name',
                content: billingInfo?.businessName,
            },
            {
                key: 'commercialName',
                title: 'Commercial name',
                content: billingInfo?.commercialName,
            },
            {
                key: 'address',
                title: 'Company address',
                content: billingInfo?.address,
            },
            {
                key: 'email',
                title: 'Email',
                content: billingInfo?.email,
            },
            {
                key: 'countryCode',
                title: 'Country code',
                content: billingInfo?.countryCode,
            },
            {
                key: 'phone',
                title: 'Mobile',
                content: billingInfo?.phone,
            },
        ];

        const billingInfoFromCountry = getBillingInfoFromCountry(country, billingInfo);

        return [...content, ...billingInfoFromCountry];
    }, [billingInfo, country]);

    const fetchBillingInfo = async () => {
        try {
            setIsLoading(true);
            const res = await getWorkspaceBillingInfo(workspaceID);
            form.setFieldsValue(res.data.data);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        const formData = form.getFieldsValue();

        const payload = {
            registeredBy: ownerID,
            workspaceID,
            country,
            ...formData,
        };

        try {
            setIsSaving(true);

            await form.validateFields();

            const res = await updateWorkspaceBillingInfo(payload as TUpdateWorkspaceBillingInfo);

            form.setFieldsValue(res.data.data);
            message.success('Billing info updated successfully');
        } catch (error) {
            console.error(error);
            message.error('Error updating billing info');
        } finally {
            setIsSaving(false);
            setIsEditing(false);
        }
    };

    useEffect(() => {
        fetchBillingInfo();
    }, [workspaceID]);

    return (
        <CardDataBase
            title="Billing details"
            style={{ minHeight: 345 }}
            innerTitleContentStyle={{ justifyContent: 'space-between', width: '100%' }}
            action={[
                <Row>
                    {isEditing ? (
                        <Row style={{ gap: 16 }}>
                            <Button onClick={() => setIsEditing(false)}>Cancel</Button>
                            <Button onClick={handleSave} loading={isSaving} type="primary">Save</Button>
                        </Row>
                    ) : (
                        <Button onClick={() => setIsEditing(true)}>Edit</Button>
                    )}
                </Row>,
            ]}
        >
            {isLoading ? <Spin style={{ width: '100%', height: '100%', marginTop: 50 }} /> : (
                <Form form={form} style={{ marginTop: 24 }}>
                    {CARD_CONTENT.map((data) => (
                        <Col span={24} key={data.key} style={{ padding: 0 }}>
                            <Form.Item name={data.key} rules={[{ required: true, message: 'Required' }]}>
                                <InputEditable
                                    inputComponent={Input}
                                    label={data.title}
                                    placeholder={data.title}
                                    type="text"
                                    editing={isEditing}
                                    labelType={false}
                                    rowStyle={{ margin: 0, padding: 0 }}
                                    colStyle={{ margin: 0, padding: 0 }}
                                />
                            </Form.Item>
                        </Col>
                    ))}
                </Form>
            )}
        </CardDataBase>
    );
};

export default BillingDetail;
